import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  Building2, RefreshCw, Pause, Play, DollarSign, Trash2, RotateCcw,
  Loader2, Search, X, Users, Truck, Package, AlertTriangle, History, Wallet,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  active:    { label: 'Active',    color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  trial:     { label: 'Trial',     color: 'bg-blue-100 text-blue-700 border-blue-200',           dot: 'bg-blue-500' },
  past_due:  { label: 'Past Due',  color: 'bg-amber-100 text-amber-800 border-amber-200',        dot: 'bg-amber-500' },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-700 border-red-200',              dot: 'bg-red-500' },
  cancelled: { label: 'Cancelled', color: 'bg-slate-100 text-slate-700 border-slate-200',        dot: 'bg-slate-400' },
};

const fmtUSD = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const StatusBadge = ({ status, isDeleted }) => {
  if (isDeleted) {
    return (
      <span
        data-testid="status-badge-deleted"
        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold border bg-slate-900 text-white border-slate-900"
        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
      >
        <Trash2 className="w-3 h-3" /> Deleted
      </span>
    );
  }
  const m = STATUS_META[status] || STATUS_META.active;
  return (
    <span
      data-testid={`status-badge-${status}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-semibold border ${m.color}`}
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
};

const SuperAdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | suspended | deleted
  const [showDeleted, setShowDeleted] = useState(true);

  // Modal state — only one open at a time.
  const [modal, setModal] = useState(null); // { kind: 'suspend'|'activate'|'credit'|'delete'|'restore'|'ledger', company }
  const [modalReason, setModalReason] = useState('');
  const [modalAmount, setModalAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/companies/manage`, { withCredentials: true });
      setCompanies(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const openModal = (kind, company) => {
    setModal({ kind, company });
    setModalReason('');
    setModalAmount('');
    if (kind === 'ledger') {
      loadLedger(company);
    }
  };

  const closeModal = () => {
    if (busy) return;
    setModal(null);
    setLedger([]);
  };

  const loadLedger = async (company) => {
    setLedgerLoading(true);
    setLedger([]);
    try {
      const r = await axios.get(`${API}/admin/companies/${company.id}/credit-ledger`, { withCredentials: true });
      setLedger(r.data || []);
    } catch (e) {
      toast.error('Failed to load credit ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  const applyAction = async () => {
    if (!modal) return;
    const { kind, company } = modal;
    setBusy(true);
    try {
      if (kind === 'suspend') {
        await axios.post(`${API}/admin/companies/${company.id}/suspend`,
          { reason: modalReason || undefined }, { withCredentials: true });
        toast.success(`${company.name} suspended.`);
      } else if (kind === 'activate') {
        await axios.post(`${API}/admin/companies/${company.id}/activate`,
          { reason: modalReason || undefined }, { withCredentials: true });
        toast.success(`${company.name} activated.`);
      } else if (kind === 'credit') {
        const amt = Number(modalAmount);
        if (!amt || amt <= 0) {
          toast.error('Enter a positive credit amount');
          setBusy(false);
          return;
        }
        await axios.post(`${API}/admin/companies/${company.id}/credit`,
          { amount: amt, reason: modalReason || undefined }, { withCredentials: true });
        toast.success(`${fmtUSD(amt)} credit added to ${company.name}.`);
      } else if (kind === 'delete') {
        await axios.delete(`${API}/admin/companies/${company.id}`,
          { params: { reason: modalReason || undefined }, withCredentials: true });
        toast.success(`${company.name} has been deleted. Tenant users can no longer log in.`);
      } else if (kind === 'restore') {
        await axios.post(`${API}/admin/companies/${company.id}/restore`, {}, { withCredentials: true });
        toast.success(`${company.name} restored. It is currently suspended — activate it next.`);
      }
      setModal(null);
      setModalReason('');
      setModalAmount('');
      fetchCompanies();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const filtered = useMemo(() => {
    return companies.filter((c) => {
      if (!showDeleted && c.is_deleted) return false;
      if (statusFilter !== 'all') {
        if (statusFilter === 'deleted') {
          if (!c.is_deleted) return false;
        } else {
          if (c.is_deleted) return false;
          const s = c.subscription?.status || 'active';
          if (s !== statusFilter) return false;
        }
      }
      if (query) {
        const q = query.toLowerCase();
        return (c.name || '').toLowerCase().includes(q)
            || (c.email || '').toLowerCase().includes(q)
            || (c.phone || '').toLowerCase().includes(q);
      }
      return true;
    });
  }, [companies, query, statusFilter, showDeleted]);

  const counts = useMemo(() => {
    return {
      total: companies.length,
      active: companies.filter((c) => !c.is_deleted && (c.subscription?.status === 'active' || c.subscription?.status === 'trial')).length,
      suspended: companies.filter((c) => !c.is_deleted && c.subscription?.status === 'suspended').length,
      deleted: companies.filter((c) => c.is_deleted).length,
      total_credit: companies.reduce((sum, c) => sum + Number(c.wallet_credit || 0), 0),
    };
  }, [companies]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="superadmin-companies-page">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-[#002FA7]/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#002FA7]" />
            </div>
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Companies
              </h1>
              <p className="text-base text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Full lifecycle control — suspend, activate, credit wallets, and remove tenants.
              </p>
            </div>
          </div>
          <button
            onClick={fetchCompanies}
            data-testid="companies-refresh"
            className="flex items-center gap-2 px-3 py-2 border border-slate-300 rounded-md text-sm text-slate-700 hover:bg-slate-50"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <button
            data-testid="filter-all"
            onClick={() => setStatusFilter('all')}
            className={`text-left bg-white border rounded-md p-4 transition ${statusFilter === 'all' ? 'border-[#002FA7] ring-1 ring-[#002FA7]' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Total</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{counts.total}</p>
          </button>
          <button
            data-testid="filter-active"
            onClick={() => setStatusFilter('active')}
            className={`text-left bg-white border rounded-md p-4 transition ${statusFilter === 'active' ? 'border-[#002FA7] ring-1 ring-[#002FA7]' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Active/Trial</p>
            <p className="text-2xl font-bold text-emerald-600 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{counts.active}</p>
          </button>
          <button
            data-testid="filter-suspended"
            onClick={() => setStatusFilter('suspended')}
            className={`text-left bg-white border rounded-md p-4 transition ${statusFilter === 'suspended' ? 'border-[#002FA7] ring-1 ring-[#002FA7]' : 'border-slate-200 hover:border-slate-300'}`}
          >
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Suspended</p>
            <p className="text-2xl font-bold text-red-600 mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{counts.suspended}</p>
          </button>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <Wallet className="w-3 h-3 inline mr-1" /> Total Credit
            </p>
            <p className="text-2xl font-bold text-[#002FA7] mt-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>{fmtUSD(counts.total_credit)}</p>
          </div>
        </div>

        {/* Search + show deleted toggle */}
        <div className="bg-white border border-slate-200 rounded-md mb-6">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input
              data-testid="companies-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search company name, email, phone…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            />
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <input
                type="checkbox"
                data-testid="companies-show-deleted"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
                className="accent-[#002FA7]"
              />
              Show deleted ({counts.deleted})
            </label>
          </div>

          {loading ? (
            <div className="px-6 py-10 flex items-center justify-center text-slate-500 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading companies…
            </div>
          ) : filtered.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              No companies match your filters.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Company</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Status</th>
                    <th className="text-left py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Plan</th>
                    <th className="text-right py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Wallet</th>
                    <th className="text-center py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Tenant Size</th>
                    <th className="text-right py-2.5 px-4 text-[11px] font-semibold uppercase tracking-widest text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const s = c.subscription?.status || 'active';
                    const isDeleted = c.is_deleted;
                    return (
                      <tr key={c.id} data-testid={`company-row-${c.id}`} className={`border-b border-slate-100 ${isDeleted ? 'bg-slate-50/70' : 'hover:bg-slate-50/50'}`}>
                        <td className="py-3 px-4">
                          <p className={`font-semibold ${isDeleted ? 'text-slate-500 line-through' : 'text-slate-900'}`}>{c.name || '—'}</p>
                          <p className="text-xs text-slate-500">{c.email || c.phone || '—'}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5">Created {fmtDate(c.created_at)}</p>
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={s} isDeleted={isDeleted} />
                          {c.subscription?.override_by_admin && !isDeleted && (
                            <p className="text-[10px] text-slate-500 mt-1 uppercase tracking-widest">Manual override</p>
                          )}
                          {isDeleted && (
                            <p className="text-[10px] text-slate-500 mt-1">Deleted {fmtDate(c.deleted_at)}</p>
                          )}
                        </td>
                        <td className="py-3 px-4 text-slate-700">{c.subscription?.plan_name || '—'}</td>
                        <td className="py-3 px-4 text-right">
                          <p className={`font-semibold ${Number(c.wallet_credit) > 0 ? 'text-[#002FA7]' : 'text-slate-400'}`}>
                            {fmtUSD(c.wallet_credit)}
                          </p>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-center gap-3 text-[11px] text-slate-600">
                            <span title="Users" className="flex items-center gap-1"><Users className="w-3 h-3" />{c.user_count}</span>
                            <span title="Drivers" className="flex items-center gap-1"><Truck className="w-3 h-3" />{c.driver_count}</span>
                            <span title="Loads" className="flex items-center gap-1"><Package className="w-3 h-3" />{c.load_count}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                            <button
                              data-testid={`ledger-${c.id}`}
                              onClick={() => openModal('ledger', c)}
                              className="px-2 py-1 rounded text-xs text-slate-600 hover:bg-slate-100"
                              title="View credit ledger"
                            >
                              <History className="w-3.5 h-3.5 inline" /> Ledger
                            </button>
                            {isDeleted ? (
                              <button
                                data-testid={`restore-${c.id}`}
                                onClick={() => openModal('restore', c)}
                                className="px-2 py-1 rounded text-xs font-semibold text-emerald-700 hover:bg-emerald-50 border border-emerald-200"
                              >
                                <RotateCcw className="w-3.5 h-3.5 inline" /> Restore
                              </button>
                            ) : (
                              <>
                                {s === 'suspended' ? (
                                  <button
                                    data-testid={`activate-${c.id}`}
                                    onClick={() => openModal('activate', c)}
                                    className="px-2 py-1 rounded text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                                  >
                                    <Play className="w-3.5 h-3.5 inline" /> Activate
                                  </button>
                                ) : (
                                  <button
                                    data-testid={`suspend-${c.id}`}
                                    onClick={() => openModal('suspend', c)}
                                    className="px-2 py-1 rounded text-xs font-semibold text-amber-700 hover:bg-amber-50"
                                  >
                                    <Pause className="w-3.5 h-3.5 inline" /> Suspend
                                  </button>
                                )}
                                <button
                                  data-testid={`credit-${c.id}`}
                                  onClick={() => openModal('credit', c)}
                                  className="px-2 py-1 rounded text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7]/10"
                                >
                                  <DollarSign className="w-3.5 h-3.5 inline" /> Credit
                                </button>
                                <button
                                  data-testid={`delete-${c.id}`}
                                  onClick={() => openModal('delete', c)}
                                  className="px-2 py-1 rounded text-xs font-semibold text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-3.5 h-3.5 inline" /> Delete
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Action modal */}
      {modal && modal.kind !== 'ledger' && (
        <ActionModal
          modal={modal}
          modalReason={modalReason}
          setModalReason={setModalReason}
          modalAmount={modalAmount}
          setModalAmount={setModalAmount}
          busy={busy}
          onClose={closeModal}
          onApply={applyAction}
        />
      )}

      {/* Ledger modal */}
      {modal && modal.kind === 'ledger' && (
        <LedgerModal
          company={modal.company}
          ledger={ledger}
          loading={ledgerLoading}
          onClose={closeModal}
        />
      )}
    </DashboardLayout>
  );
};

// ---- Inline subcomponents kept here for clarity (single-page feature) ----
const ActionModal = ({ modal, modalReason, setModalReason, modalAmount, setModalAmount, busy, onClose, onApply }) => {
  const { kind, company } = modal;
  const titles = {
    suspend:  { title: `Suspend ${company.name}?`, desc: 'Drivers and dispatchers go read-only. The company admin can still log in to settle billing. All users will be notified in-app.', cta: 'Suspend', tone: 'amber' },
    activate: { title: `Activate ${company.name}?`, desc: 'Restores full access for all users in this tenant. Clears any manual suspend override.', cta: 'Activate', tone: 'emerald' },
    credit:   { title: `Add credit to ${company.name}`, desc: 'This dollar amount is added to the tenant wallet and automatically applied to their next Stripe checkout.', cta: 'Add credit', tone: 'primary' },
    delete:   { title: `Delete ${company.name}?`, desc: 'All tenant users will be blocked from logging in. The company and its data are retained indefinitely and can be restored later from this page.', cta: 'Delete company', tone: 'red' },
    restore:  { title: `Restore ${company.name}?`, desc: 'The company is restored in suspended state. Users still cannot mutate data until you activate the subscription.', cta: 'Restore', tone: 'emerald' },
  };
  const t = titles[kind];
  const toneCls = {
    amber:    'bg-amber-600 hover:bg-amber-700',
    emerald:  'bg-emerald-600 hover:bg-emerald-700',
    primary:  'bg-[#002FA7] hover:bg-[#00247D]',
    red:      'bg-red-600 hover:bg-red-700',
  }[t.tone];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose} data-testid="companies-action-modal">
      <div className="bg-white rounded-lg w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{t.title}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="companies-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="flex items-start gap-2 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {kind === 'delete' && <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />}
            <p>{t.desc}</p>
          </div>
          {kind === 'credit' && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">
                Credit amount (USD)
              </label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                placeholder="50.00"
                value={modalAmount}
                onChange={(e) => setModalAmount(e.target.value)}
                data-testid="companies-modal-amount"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
                autoFocus
              />
              <p className="text-[11px] text-slate-500 mt-1.5">Current wallet: <strong>{`$${Number(company.wallet_credit || 0).toFixed(2)}`}</strong></p>
            </div>
          )}
          {kind !== 'restore' && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">
                Reason {kind === 'credit' ? '(internal note)' : '(shown to users)'}
              </label>
              <textarea
                rows={3}
                value={modalReason}
                onChange={(e) => setModalReason(e.target.value)}
                placeholder={
                  kind === 'suspend' ? 'e.g. Multiple failed invoices' :
                  kind === 'activate' ? 'e.g. Bank transfer received' :
                  kind === 'credit' ? 'e.g. Service credit for downtime' :
                  kind === 'delete' ? 'e.g. Account terminated by request' : ''
                }
                data-testid="companies-modal-reason"
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              />
            </div>
          )}
        </div>
        <div className="border-t border-slate-100 px-6 py-3 flex items-center justify-end gap-2">
          <button
            disabled={busy}
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md"
            data-testid="companies-modal-cancel"
          >
            Cancel
          </button>
          <button
            disabled={busy}
            onClick={onApply}
            data-testid="companies-modal-confirm"
            className={`px-4 py-2 text-sm font-semibold text-white rounded-md disabled:opacity-50 flex items-center gap-2 ${toneCls}`}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t.cta}
          </button>
        </div>
      </div>
    </div>
  );
};

const LedgerModal = ({ company, ledger, loading, onClose }) => (
  <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose} data-testid="companies-ledger-modal">
    <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Credit ledger</h2>
          <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {company.name} — Current balance: <strong>{fmtUSD(company.wallet_credit)}</strong>
          </p>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="companies-ledger-close">
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="px-6 py-4 overflow-y-auto">
        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-500 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : ledger.length === 0 ? (
          <p className="text-sm text-slate-500 py-6 text-center" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>No credit transactions yet.</p>
        ) : (
          <ol className="space-y-3" data-testid="companies-ledger-list">
            {ledger.map((e) => (
              <li key={e.id} data-testid={`ledger-item-${e.id}`} className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3 last:border-b-0">
                <div>
                  <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {e.direction === 'credit' ? '+' : '−'}{fmtUSD(e.amount)}
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-500">{e.direction}</span>
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">{e.reason || '—'}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {e.actor_email ? `by ${e.actor_email} • ` : ''}{fmtDateTime(e.created_at)}
                  </p>
                </div>
                <p className="text-xs text-slate-500 whitespace-nowrap">
                  Balance: <strong className="text-slate-900">{fmtUSD(e.balance_after)}</strong>
                </p>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  </div>
);

export default SuperAdminCompanies;
